<template>
    <LoadingComponent :props="loading" />
    <div class="col-12">
        <div class="db-card p-4">
            <div class="flex flex-col items-start justify-start">
                <div class="flex flex-wrap justify-start items-center border-b w-full gap-y-2 gap-x-6 pb-3 mb-4">
                    <p class="text-2xl font-medium font-client mt-0.5">
                        {{ $t('label.order') }}
                        <span class="text-primary">
                            #{{ order.order_serial_no }}
                        </span>
                    </p>
                    <div class="flex items-center gap-2">
                        <span :class="'db-table-badge px-2 rounded-3xl ' + statusClass(order.payment_status)">
                            {{ $t('label.payment_status') }}: {{ enums.paymentStatusEnumArray[order.payment_status] }}
                        </span>
                        <span :class="'db-table-badge px-2 rounded-3xl ' + orderStatusClass(order.status)">
                            {{ $t('label.order_status') }}: {{ enums.orderStatusEnumArray[order.status] }}
                        </span>
                    </div>
                </div>
                <div class="w-full flex flex-wrap items-center justify-between gap-4">
                    <div class="md:w-auto">
                        <ul class="flex items-center gap-2">
                            <li class="flex items-center gap-2 py-2 pr-2 border-r">
                                <i class="lab lab-line-calendar lab-font-size-16"></i>
                                <span class="text-xs">{{ order.order_datetime }}</span>
                            </li>
                            <li class="text-xs py-2 pr-2 border-r">
                                {{ $t('label.payment_type') }}:
                                <span class="text-heading">
                                    {{ order.payment_method_name }}
                                </span>
                            </li>
                            <li class="text-xs py-2 pr-2" :class="shippingGuideOrTracking ? 'border-r' : ''">
                                {{ $t('label.order_type') }}:
                                <span class="text-heading">
                                    {{ enums.orderTypeEnumArray[order.order_type] }}
                                </span>
                            </li>
                            <li class="text-xs py-2 pr-2" v-if="shippingGuideOrTracking">
                                {{ $t('label.shipping_tracking_number') }}:
                                <span class="text-heading">
                                    {{ shippingGuideOrTracking }}
                                </span>
                            </li>
                        </ul>
                    </div>
                    <div class="w-auto flex flex-nowrap gap-3" v-if="order.status === enums.orderStatusEnum.PENDING">
                        <OnlineOrderReasonComponent />
                        <button type="button" @click="changeStatus(enums.orderStatusEnum.CONFIRMED)"
                            class="flex items-center justify-center text-white gap-2 px-4 py-1.5 rounded-md bg-success">
                            <i class="lab lab-fill-save"></i>
                            <span class="text-sm capitalize text-white">{{ $t('button.accept') }}</span>
                        </button>
                    </div>

                    <div v-else-if="order.status !== enums.orderStatusEnum.REJECTED && order.status !== enums.orderStatusEnum.CANCELED" class="flex flex-nowrap justify-start items-center gap-3">
                        <button v-if="canRegenerateShipment" type="button" @click="regenerateShipment"
                            class="flex items-center justify-center text-white gap-2 px-4 h-[38px] rounded shadow-db-card bg-primary">
                            <i class="lab lab-line-reload"></i>
                            <span class="text-sm capitalize text-white">{{ $t('button.regenerate_shipping_label') }}</span>
                        </button>

                        <vue-select
                            class="db-field-control f-b-custom-select !w-150"
                            v-model="payment_status"
                            :options="enums.paymentStatusObject"
                            label-by="name"
                            value-by="value"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                            @update:modelValue="changePaymentStatus"
                        />

                        <vue-select
                            v-if="order.order_type === enums.orderTypeEnum.DELIVERY"
                            class="db-field-control f-b-custom-select !w-150"
                            v-model="order_status"
                            :options="enums.orderStatusObject"
                            label-by="name"
                            value-by="value"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                            @update:modelValue="orderStatus"
                        />

                        <vue-select
                            v-if="order.order_type === enums.orderTypeEnum.PICK_UP"
                            class="db-field-control f-b-custom-select !w-150"
                            v-model="order_status"
                            :options="enums.orderStatusPickupObject"
                            label-by="name"
                            value-by="value"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                            @update:modelValue="orderStatus"
                        />

                        <OnlineOrderReceiptComponent :order="order" :orderProducts="orderProducts" :orderUser="orderUser"
                            :orderAddress="orderAddress" />
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="col-12 sm:col-6">
        <div class="row">
            <div class="col-12">
                <div class="db-card p-1">
                    <div class="db-card-header py-3">
                        <h3 class="db-card-title">{{ $t('label.order_resume') }}</h3>
                    </div>
                    <div class="db-card-body px-2 py-1">
                        <ul class="flex flex-col gap-2 p-3 border-b border-dashed">
                            <li class="flex items-center justify-between">
                                <span class="text-sm font-medium">{{ $t('label.subtotal') }}</span>
                                <span class="text-sm font-medium">{{ order.subtotal_currency_price }}</span>
                            </li>
                            <li class="flex items-center justify-between">
                                <span class="text-sm font-medium">{{ $t('label.tax_fee') }}</span>
                                <span class="text-sm font-medium">{{ order.tax_currency_price }}</span>
                            </li>
                            <li class="flex items-center justify-between">
                                <span class="text-sm font-medium">{{ $t('label.discount') }}</span>
                                <span class="text-sm font-medium">{{ order.discount_currency_price }}</span>
                            </li>
                            <li class="flex items-center justify-between">
                                <span class="text-sm font-medium">{{ $t('label.shipping_charge') }}</span>
                                <span class="text-sm font-medium">
                                    {{ order.shipping_charge_currency_price }}
                                </span>
                            </li>
                            <li class="flex items-center justify-between" v-if="order.shipping_quote && order.shipping_quote.provider">
                                <span class="text-sm font-medium">{{ $t('label.shipping_provider') }}</span>
                                <span class="text-sm font-medium">{{ shippingProviderLabel(order.shipping_quote.provider) }}</span>
                            </li>
                            <li class="flex items-center justify-between" v-if="order.shipping_quote && order.shipping_quote.service_level">
                                <span class="text-sm font-medium">{{ $t('label.shipping_service') }}</span>
                                <span class="text-sm font-medium">{{ order.shipping_quote.service_level }}</span>
                            </li>
                            <li class="flex items-center justify-between" v-if="order.shipping_quote && order.shipping_quote.days">
                                <span class="text-sm font-medium">{{ $t('label.shipping_days') }}</span>
                                <span class="text-sm font-medium">{{ order.shipping_quote.days }}</span>
                            </li>
                            <li class="flex items-center justify-between" v-if="order.shipping_shipment && order.shipping_shipment.tracking_number">
                                <span class="text-sm font-medium">{{ $t('label.shipping_tracking_number') }}</span>
                                <span class="text-sm font-medium">{{ order.shipping_shipment.tracking_number }}</span>
                            </li>
                            <li class="flex items-center justify-between" v-if="order.shipping_shipment && order.shipping_shipment.label_url">
                                <span class="text-sm font-medium">{{ $t('label.shipping_label') }}</span>
                                <a class="text-sm font-medium text-primary" :href="order.shipping_shipment.label_url" target="_blank">{{ $t('label.view') }}</a>
                            </li>
                        </ul>
                        <div class="flex items-center justify-between p-3">
                            <h4 class="text-sm font-medium">{{ $t('label.total') }}</h4>
                            <h5 class="text-sm font-medium">
                                {{ order.total_currency_price }}
                            </h5>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-12">
                <div class="db-card p-1">
                    <div class="db-card-header py-3">
                        <h3 class="db-card-title">{{ $t('label.order_items') }}</h3>
                    </div>
                    <div class="db-card-body">
                        <div class="pl-3">
                            <div class="mb-3 pb-3 border-b last:mb-0 last:pb-0 last:border-b-0 border-gray-2"
                                v-if="orderProducts.length > 0" v-for="product in orderProducts" :key="product">
                                <div class="flex items-center gap-3 relative">
                                    <h3
                                        class="absolute top-5 ltr:-left-3 rtl:-right-3 text-sm w-[26px] h-[26px] leading-[26px] text-center rounded-full text-white bg-heading">
                                        {{ product.quantity }}</h3>
                                    <img class="w-16 h-16 rounded-lg flex-shrink-0" :src="product.product_image"
                                        alt="thumbnail">
                                    <div class="flex flex-col justify-center items-start overflow-hidden">
                                        <h4 :class="!product.variation_names ? '' : ''"
                                            class="text-sm capitalize whitespace-nowrap overflow-hidden text-ellipsis">
                                            {{ product.product_name }}</h4>
                                        <p class="text-sm overflow-hidden">{{ product.variation_names }}</p>
                                        <div class="flex flex-wrap items-center justify-between gap-4">
                                            <div class="flex items-center gap-4">
                                                <span class="text-sm font-semibold">{{
                                                    product.subtotal_currency_price
                                                }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12" v-if="order.status === enums.orderStatusEnum.REJECTED">
                <div class="db-card bg-red-100 border border-danger">
                    <div class="db-card-header border-danger py-3">
                        <h3 class="db-card-title text-danger">{{ $t('label.reject_reason') }}</h3>
                    </div>
                    <div class="db-card-body">
                        <p class="text-sm leading-5 text-danger">{{ order.reason }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="col-12 sm:col-6">
        <div class="row">

            <div class="col-12" v-if="deliveries.length > 0 && order.status !== enums.orderStatusEnum.REJECTED">
                <div class="db-card p-1">
                    <div class="db-card-header border-none py-3">
                        <button
                            type="button"
                            class="w-full flex items-center justify-between"
                            @click="toggleAccordion('shipping-deliveries')">
                            <h3 class="db-card-title">{{ $t('label.shipping_events') }}</h3>
                            <i class="lab lab-line-chevron-down lab-font-size-16 transition-transform duration-200"
                                :class="isAccordionOpen('shipping-deliveries') ? 'rotate-180' : ''"></i>
                        </button>
                    </div>
                    <div class="db-card-body border-t" v-show="isAccordionOpen('shipping-deliveries')">
                        <div class="grid gap-4">
                            <div v-for="delivery in deliveries" :key="delivery.id" class="rounded-lg border p-4">
                                <div class="flex items-center justify-between gap-2 border-b pb-2 mb-4">
                                    <h4 class="text-md font-client font-medium">{{ deliveryTypeLabel(delivery.delivery_type) }}</h4>
                                    <span class="text-xs px-2 py-1 rounded-3xl bg-primary/10 text-primary">{{ shippingDeliveryStatusLabel(delivery.status) }}</span>
                                </div>

                                <div class="grid sm:grid-cols-2 gap-3" v-if="deliveryForms[delivery.id]">
                                    <div>
                                        <label class="db-field-title">{{ $t('label.event') }}</label>
                                        <select v-model="deliveryForms[delivery.id].event_name" class="db-field-control">
                                            <option v-for="eventOption in manualDeliveryEventNameOptions" :key="`event-${eventOption.value}`" :value="eventOption.value">
                                                {{ $t(eventOption.label) }}
                                            </option>
                                        </select>
                                    </div>
                                    <div>
                                        <label class="db-field-title">{{ $t('label.event_type') }}</label>
                                        <select v-model="deliveryForms[delivery.id].event_status" class="db-field-control">
                                            <option v-for="eventStatusOption in manualDeliveryEventStatusOptions" :key="`event-status-${eventStatusOption.value}`" :value="eventStatusOption.value">
                                                {{ $t(eventStatusOption.label) }}
                                            </option>
                                        </select>
                                    </div>
                                    <div>
                                        <label class="db-field-title">{{ $t('label.shipping_tracking_number') }}</label>
                                        <input
                                            v-model="deliveryForms[delivery.id].tracking_number"
                                            type="text"
                                            class="db-field-control"
                                            :placeholder="$t('label.example_tracking_number_placeholder')"
                                        />
                                    </div>
                                    <div>
                                        <label class="db-field-title">{{ $t('label.shipping_tracking_url') }}</label>
                                        <input
                                            v-model="deliveryForms[delivery.id].tracking_url"
                                            type="text"
                                            class="db-field-control"
                                            :placeholder="$t('label.example_tracking_url_placeholder')"
                                        />
                                    </div>
                                    <div>
                                        <label class="db-field-title">{{ $t('label.status') }}</label>
                                        <select v-model="deliveryForms[delivery.id].status" class="db-field-control">
                                            <option v-for="statusOption in manualDeliveryStatusOptions" :key="`status-${statusOption.value}`" :value="statusOption.value">
                                                {{ $t(statusOption.label) }}
                                            </option>
                                        </select>
                                    </div>
                                    <div>
                                        <label class="db-field-title">{{ $t('label.message') }}</label>
                                        <input
                                            v-model="deliveryForms[delivery.id].event_message"
                                            type="text"
                                            class="db-field-control"
                                            :placeholder="$t('label.example_event_message_placeholder')"
                                        />
                                    </div>
                                </div>
                                <div v-else class="text-xs text-slate-500">
                                    {{ $t('label.loading') }}...
                                </div>

                                <div class="flex justify-end border-t mt-4 pt-4">
                                    <button type="button" class="db-btn text-white bg-primary" @click="updateDelivery(delivery)">
                                        <i class="lab lab-fill-save"></i>
                                        <span>{{ $t('button.save') }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-12" v-if="order.shipping_events && order.shipping_events.length > 0">
                <div class="db-card p-1">
                    <div class="db-card-header border-none py-3">
                        <button
                            type="button"
                            class="w-full flex items-center justify-between"
                            @click="toggleAccordion('shipping-events')">
                            <h3 class="db-card-title">{{ $t('label.shipping_follow_up') }}</h3>
                            <i class="lab lab-line-chevron-down lab-font-size-16 transition-transform duration-200"
                                :class="isAccordionOpen('shipping-events') ? 'rotate-180' : ''"></i>
                        </button>
                    </div>
                    <div class="db-card-body border-t overflow-x-auto" v-show="isAccordionOpen('shipping-events')">
                        <table class="w-full text-xs">
                            <thead>
                                <tr class="border-b">
                                    <th class="text-left py-2 uppercase">{{ $t('label.event_date_time') }}</th>
                                    <th class="text-left py-2 uppercase">{{ $t('label.source') }}</th>
                                    <th class="text-left py-2 uppercase">{{ $t('label.event') }}</th>
                                    <th class="text-left py-2 uppercase">{{ $t('label.status') }}</th>
                                    <th class="text-left py-2 uppercase">{{ $t('label.message') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="event in order.shipping_events" :key="event.id" class="border-b last:border-0">
                                    <td class="py-2 pr-2">{{ event.created_at }}</td>
                                    <td class="py-2 pr-2">{{ shippingEventSourceLabel(event.source) }}</td>
                                    <td class="py-2 pr-2">{{ shippingEventNameLabel(event.event) }}</td>
                                    <td class="py-2 pr-2">
                                        <span class="px-2 py-1 rounded-3xl text-[11px] font-semibold" :class="shippingEventStatusBadgeClass(event.status)">
                                            {{ shippingEventStatusLabel(event.status) }}
                                        </span>
                                    </td>
                                    <td class="py-2 pr-2">{{ shippingEventMessageLabel(event) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="col-12" v-if="order.order_type === enums.orderTypeEnum.DELIVERY && orderAddress.length > 0 && order.status !== enums.orderStatusEnum.REJECTED"
                v-for="(address, addressIndex) in orderAddress" :key="`address-${addressIndex}`">
                <div class="db-card p-1">
                    <div class="db-card-header border-none py-3">
                        <button
                            type="button"
                            class="w-full flex items-center justify-between"
                            @click="toggleAccordion(`address-${addressIndex}`)">
                            <h3 class="db-card-title">
                                {{ address.address_type === enums.addressTypeEnum.SHIPPING ? $t('label.shipping_address') : $t('label.billing_address') }}
                            </h3>
                            <i class="lab lab-line-chevron-down lab-font-size-16 transition-transform duration-200"
                                :class="isAccordionOpen(`address-${addressIndex}`) ? 'rotate-180' : ''"></i>
                        </button>
                    </div>
                    <div class="db-card-body border-t" v-show="isAccordionOpen(`address-${addressIndex}`)">
                        <div class="flex items-center gap-3 mb-4">
                            <img class="w-8 rounded-full" alt="avatar" :src="orderUser.image">
                            <h4 class="font-semibold text-sm capitalize">
                                {{ textShortener(address.full_name, 20) }}
                            </h4>
                        </div>
                        <ul class="flex flex-col gap-3 pt-4 border-t">
                            <li v-if="address.email" class="flex items-center gap-2.5">
                                <i class="lab lab-line-mail lab-font-size-14"></i>
                                <span class="text-xs">{{ address.email }}</span>
                            </li>
                            <li class="flex items-center gap-2.5" v-if="address.phone">
                                <i class="lab lab-line-call-calling lab-font-size-14"></i>
                                <span class="text-xs" dir="ltr">{{ address.country_code + '' + address.phone }}</span>
                            </li>

                            <li class="flex items-center gap-2.5">
                                <i class="lab lab-line-location lab-font-size-14"></i>
                                <span class="text-xs">
                                    <span v-if="address.address">{{ address.address }},</span>
                                    <span v-if="address.city">{{ address.city }},</span>
                                    <span v-if="address.state">{{ address.state }},</span>
                                    <span v-if="address.country">{{ address.country }},</span>
                                    <span v-if="address.zip_code">{{ address.zip_code }}</span>
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div class="col-12" v-else-if="order.order_type === enums.orderTypeEnum.PICK_UP && order.status !== enums.orderStatusEnum.REJECTED">
                <div class="db-card p-1">
                    <div class="db-card-header border-none py-3">
                        <button
                            type="button"
                            class="w-full flex items-center justify-between"
                            @click="toggleAccordion('pickup-address')">
                            <h3 class="db-card-title">
                                {{ $t('label.pick_up_address') }}
                            </h3>
                            <i class="lab lab-line-chevron-down lab-font-size-16 transition-transform duration-200"
                                :class="isAccordionOpen('pickup-address') ? 'rotate-180' : ''"></i>
                        </button>
                    </div>

                    <div class="db-card-body border-t" v-show="isAccordionOpen('pickup-address')">
                        <div class="flex items-center gap-3 mb-4">
                            <img class="w-8 rounded-full" alt="avatar" :src="orderUser.image">
                            <h4 class="font-semibold text-sm capitalize">
                                {{ textShortener(orderUser.name, 20) }}
                            </h4>
                        </div>
                        <ul class="flex flex-col gap-3 pt-4 border-t">
                            <li v-if="orderUser.email" class="flex items-center gap-2.5">
                                <i class="lab lab-line-mail lab-font-size-14"></i>
                                <span class="text-xs">{{ orderUser.email }}</span>
                            </li>
                            <li v-if="orderUser.phone" class="flex items-center gap-2.5">
                                <i class="lab lab-line-call-calling lab-font-size-14"></i>
                                <span class="text-xs" dir="ltr">{{ orderUser.country_code + '' + orderUser.phone
                                    }}</span>
                            </li>
                            <li class="flex items-center gap-2.5">
                                <i class="lab lab-line-location lab-font-size-14"></i>
                                <span class="text-xs">
                                    <span v-if="outletAddress.address">{{ outletAddress.address }},</span>
                                    <span v-if="outletAddress.city">{{ outletAddress.city }},</span>
                                    <span v-if="outletAddress.state">{{ outletAddress.state }},</span>
                                    <span v-if="outletAddress.country">{{ outletAddress.country }},</span>
                                    <span v-if="outletAddress.zip_code">{{ outletAddress.zip_code }}</span>
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import LoadingComponent from "../components/LoadingComponent";
import appService from "../../../services/appService";
import paymentStatusEnum from "../../../enums/modules/paymentStatusEnum";
import addressTypeEnum from "../../../enums/modules/addressTypeEnum";
import orderStatusEnum from "../../../enums/modules/orderStatusEnum";
import orderTypeEnum from "../../../enums/modules/orderTypeEnum";
import alertService from "../../../services/alertService";
import OnlineOrderReasonComponent from "./OnlineOrderReasonComponent";
import OnlineOrderReceiptComponent from "./OnlineOrderReceiptComponent";

export default {
    name: "OnlineOrderShowComponent",
    components: {
        OnlineOrderReceiptComponent,
        LoadingComponent,
        OnlineOrderReasonComponent
    },
    data() {
        return {
            loading: {
                isActive: false
            },
            payment_status: null,
            delivery_boy: null,
            order_status: null,
            deliveryForms: {},
            openAccordionKey: null,
            enums: {
                paymentStatusEnum: paymentStatusEnum,
                addressTypeEnum: addressTypeEnum,
                paymentStatusEnumArray: {
                    [paymentStatusEnum.PAID]: this.$t("label.paid"),
                    [paymentStatusEnum.UNPAID]: this.$t("label.unpaid")
                },
                paymentStatusObject: [
                    {
                        name: this.$t("label.paid"),
                        value: paymentStatusEnum.PAID
                    },
                    {
                        name: this.$t("label.unpaid"),
                        value: paymentStatusEnum.UNPAID,
                    },
                ],
                orderStatusEnum: orderStatusEnum,
                orderStatusEnumArray: {
                    [orderStatusEnum.PENDING]: this.$t("label.pending"),
                    [orderStatusEnum.CONFIRMED]: this.$t("label.confirmed"),
                    [orderStatusEnum.ON_THE_WAY]: this.$t("label.on_the_way"),
                    [orderStatusEnum.DELIVERED]: this.$t("label.delivered"),
                    [orderStatusEnum.CANCELED]: this.$t("label.canceled"),
                    [orderStatusEnum.REJECTED]: this.$t("label.rejected"),
                },
                orderStatusObject: [
                    {
                        name: this.$t("label.confirmed"),
                        value: orderStatusEnum.CONFIRMED,
                    },
                    {
                        name: this.$t("label.on_the_way"),
                        value: orderStatusEnum.ON_THE_WAY,
                    },
                    {
                        name: this.$t("label.delivered"),
                        value: orderStatusEnum.DELIVERED,
                    },
                ],
                orderStatusPickupObject: [
                    {
                        name: this.$t("label.confirmed"),
                        value: orderStatusEnum.CONFIRMED,
                    },
                    {
                        name: this.$t("label.delivered"),
                        value: orderStatusEnum.DELIVERED,
                    },
                ],
                orderTypeEnum: orderTypeEnum,
                orderTypeEnumArray: {
                    [orderTypeEnum.DELIVERY]: this.$t("label.delivery"),
                    [orderTypeEnum.PICK_UP]: this.$t("label.pick_up")
                },
            }
        }
    },
    computed: {
        order: function () {
            return this.$store.getters['onlineOrder/show'];
        },
        orderProducts: function () {
            return this.$store.getters['onlineOrder/orderProducts'];
        },
        orderUser: function () {
            return this.$store.getters['onlineOrder/orderUser'];
        },
        orderAddress: function () {
            return this.$store.getters['onlineOrder/orderAddress'];
        },
        outletAddress: function () {
            return this.$store.getters['onlineOrder/outletAddress'];
        },
        deliveries: function () {
            return Array.isArray(this.order?.deliveries) ? this.order.deliveries : [];
        },
        shippingGuideOrTracking: function () {
            if (!this.order || !this.order.shipping_shipment) {
                const firstDeliveryWithTracking = this.deliveries.find((delivery) => !!delivery.tracking_number);
                return firstDeliveryWithTracking ? firstDeliveryWithTracking.tracking_number : null;
            }

            return this.order.shipping_shipment.tracking_number || this.order.shipping_shipment.shipment_id || null;
        },
        canRegenerateShipment: function () {
            if (!this.order || this.order.order_type !== this.enums.orderTypeEnum.DELIVERY) {
                return false;
            }

            const currentStatus = parseInt(this.order.status);
            const isEligibleStatus = [
                this.enums.orderStatusEnum.CONFIRMED,
                this.enums.orderStatusEnum.ON_THE_WAY,
                this.enums.orderStatusEnum.DELIVERED,
            ].includes(currentStatus);

            if (!isEligibleStatus) {
                return false;
            }

            const hasShippingContext = !!this.order.shipping_quote || !!this.order.shipping_shipment;
            if (!hasShippingContext) {
                return false;
            }

            return !this.order.shipping_shipment || this.order.shipping_shipment.status !== 'created' || !this.order.shipping_shipment.label_url;
        },
        manualDeliveryStatusOptions: function () {
            return [
                { value: 'pending', label: 'label.shipping_delivery_status_pending' },
                { value: 'process', label: 'label.shipping_delivery_status_process' },
                { value: 'shipped', label: 'label.shipping_delivery_status_shipped' },
                { value: 'in_transit', label: 'label.shipping_delivery_status_in_transit' },
                { value: 'delivery_in_progress', label: 'label.shipping_delivery_status_delivery_in_progress' },
                { value: 'delivered', label: 'label.shipping_delivery_status_delivered' },
                { value: 'failed', label: 'label.shipping_delivery_status_failed' },
            ];
        },
        manualDeliveryEventNameOptions: function () {
            return [
                { value: 'manual_delivery_update', label: 'label.shipping_event_name_manual_delivery_update' },
                { value: 'package_prepared', label: 'label.shipping_event_name_package_prepared' },
                { value: 'picked_up_by_carrier', label: 'label.shipping_event_name_picked_up_by_carrier' },
                { value: 'departure_hub', label: 'label.shipping_event_name_departure_hub' },
                { value: 'arrived_destination_hub', label: 'label.shipping_event_name_arrived_destination_hub' },
                { value: 'out_for_delivery', label: 'label.shipping_event_name_out_for_delivery' },
                { value: 'delivery_attempted', label: 'label.shipping_event_name_delivery_attempted' },
                { value: 'delivery_confirmed', label: 'label.shipping_event_name_delivery_confirmed' },
                { value: 'customer_not_available', label: 'label.shipping_event_name_customer_not_available' },
                { value: 'return_to_sender_started', label: 'label.shipping_event_name_return_to_sender_started' },
                { value: 'return_to_sender_completed', label: 'label.shipping_event_name_return_to_sender_completed' },
                { value: 'incident_reported', label: 'label.shipping_event_name_incident_reported' },
            ];
        },
        manualDeliveryEventStatusOptions: function () {
            return [
                { value: 'info', label: 'label.shipping_event_status_info' },
                { value: 'action_required', label: 'label.shipping_event_status_action_required' },
            ];
        }
    },
    mounted() {
        this.loading.isActive = true;
        this.$store.dispatch('onlineOrder/show', this.$route.params.id).then(res => {
            this.payment_status = res.data.data.payment_status;
            this.order_status = res.data.data.status;
            this.syncDeliveryForms();
            this.loading.isActive = false;
        }).catch((error) => {
            this.loading.isActive = false;
        });
    },
    methods: {
        statusClass: function (status) {
            return appService.statusClass(status);
        },
        orderStatusClass: function (status) {
            return appService.orderStatusClass(status);
        },
        textShortener: function (text, number = 30) {
            return appService.textShortener(text, number);
        },
        syncDeliveryForms: function () {
            const forms = {};
            this.deliveries.forEach((delivery) => {
                forms[delivery.id] = {
                    status: delivery.status || 'pending',
                    tracking_number: delivery.tracking_number || '',
                    tracking_url: delivery.tracking_url || '',
                    event_name: 'manual_delivery_update',
                    event_status: 'info',
                    event_message: '',
                };
            });
            this.deliveryForms = forms;
        },
        deliveryTypeLabel: function (value) {
            const normalized = `${value ?? ''}`.trim().toLowerCase();
            if (normalized === 'broker_provider') {
                return this.$t('label.broker_provider_shipping');
            }

            if (normalized === 'standard_provider_flat_rate') {
                return this.$t('label.flat_rate_shipping');
            }

            if (normalized === 'standard_provider_free') {
                return this.$t('label.free_shipping');
            }

            if (normalized === 'standard_provider') {
                return this.$t('label.standard_provider_shipping');
            }

            return `${value ?? '-'}`;
        },
        updateDelivery: function (delivery) {
            const form = this.deliveryForms[delivery.id];
            if (!form) {
                return;
            }

            this.loading.isActive = true;
            this.$store.dispatch('onlineOrder/updateDelivery', {
                id: this.$route.params.id,
                form: {
                    delivery_id: delivery.id,
                    status: form.status,
                    tracking_number: form.tracking_number,
                    tracking_url: form.tracking_url,
                    event_name: form.event_name || 'manual_delivery_update',
                    event_status: form.event_status || 'info',
                    event_message: form.event_message || this.$t('message.manual_delivery_update_from_admin'),
                }
            }).then((res) => {
                this.loading.isActive = false;
                this.payment_status = res.data.data.payment_status;
                this.order_status = res.data.data.status;
                this.syncDeliveryForms();
                alertService.success(this.$t('message.shipping_update'));
            }).catch((err) => {
                this.loading.isActive = false;
                alertService.error(err.response?.data?.message ?? err.message);
            });
        },
        changeStatus: function (status) {
            appService.acceptOrder().then((res) => {
                try {
                    this.loading.isActive = true;
                    this.$store.dispatch("onlineOrder/changeStatus", {
                        id: this.$route.params.id,
                        status: status,
                    }).then((res) => {
                        this.order_status = res.data.data.status;
                        this.loading.isActive = false;
                        alertService.successFlip(
                            1,
                            this.$t("label.status")
                        );
                    }).catch((err) => {
                        this.loading.isActive = false;
                        alertService.error(err.response.data.message);
                    });
                } catch (err) {
                    this.loading.isActive = false;
                    alertService.error(err.response.data.message);
                }
            }).catch((err) => {
                this.loading.isActive = false;
            });
        },
        changePaymentStatus: function (e) {
            const paymentStatus = e?.target?.value ?? e;
            try {
                this.loading.isActive = true;
                this.$store.dispatch("onlineOrder/changePaymentStatus", {
                    id: this.$route.params.id,
                    payment_status: paymentStatus,
                }).then((res) => {
                    this.loading.isActive = false;
                    alertService.successFlip(
                        1,
                        this.$t("label.status")
                    );
                }).catch((err) => {
                    this.loading.isActive = false;
                    alertService.error(err.response.data.message);
                });
            } catch (err) {
                this.loading.isActive = false;
                alertService.error(err.response.data.message);
            }
        },
        orderStatus: function (e) {
            const status = e?.target?.value ?? e;
            try {
                this.loading.isActive = true;
                this.$store.dispatch("onlineOrder/changeStatus", {
                    id: this.$route.params.id,
                    status: status,
                }).then((res) => {
                    this.loading.isActive = false;
                    alertService.successFlip(
                        1,
                        this.$t("label.status")
                    );
                }).catch((err) => {
                    this.loading.isActive = false;
                    alertService.error(err.response.data.message);
                });
            } catch (err) {
                this.loading.isActive = false;
                alertService.error(err.response.data.message);
            }
        },
        regenerateShipment: function () {
            try {
                this.loading.isActive = true;
                this.$store.dispatch("onlineOrder/regenerateShipment", {
                    id: this.$route.params.id,
                }).then(() => {
                    this.loading.isActive = false;
                    alertService.success(this.$t('message.shipping_label_regenerated'));
                }).catch((err) => {
                    this.loading.isActive = false;
                    alertService.error(err.response?.data?.message ?? err.message);
                });
            } catch (err) {
                this.loading.isActive = false;
                alertService.error(err.response?.data?.message ?? err.message);
            }
        },
        shippingDeliveryStatusLabel: function (value) {
            return this.resolveTranslatedLabel('shipping_delivery_status', value);
        },
        shippingEventSourceLabel: function (value) {
            return this.resolveTranslatedLabel('shipping_event_source', value);
        },
        shippingProviderLabel: function (value) {
            return this.resolveTranslatedLabel('shipping_provider', value);
        },
        shippingEventNameLabel: function (value) {
            return this.resolveTranslatedLabel('shipping_event_name', value);
        },
        shippingEventStatusLabel: function (value) {
            return this.resolveTranslatedLabel('shipping_event_status', value);
        },
        shippingEventStatusBadgeClass: function (value) {
            const normalized = `${value ?? ''}`.trim().toLowerCase();
            if (normalized === 'success') {
                return 'text-[#2AC769] bg-[#E2FFEE]';
            }

            if (normalized === 'failed') {
                return 'text-[#FB4E4E] bg-[#FFE8E8]';
            }

            return 'text-primary bg-primary/10';
        },
        shippingEventMessageLabel: function (event) {
            const key = `shipping_event_message_${event?.event ?? ''}`;
            if (this.$te(`label.${key}`)) {
                return this.$t(`label.${key}`);
            }

            return event?.message ? event.message : '-';
        },
        resolveTranslatedLabel: function (prefix, value) {
            const normalized = `${value ?? ''}`.trim().toLowerCase();
            const key = `${prefix}_${normalized}`;
            if (normalized && this.$te(`label.${key}`)) {
                return this.$t(`label.${key}`);
            }

            return this.formatShippingEventFallback(value);
        },
        formatShippingEventFallback: function (value) {
            if (!value) {
                return '-';
            }

            return `${value}`
                .replace(/_/g, ' ')
                .replace(/\b\w/g, letter => letter.toUpperCase());
        },
        toggleAccordion: function (key) {
            this.openAccordionKey = this.openAccordionKey === key ? null : key;
        },
        isAccordionOpen: function (key) {
            return this.openAccordionKey === key;
        },
    }

}
</script>